import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, ShieldAlert, UserRound, Pencil, X, Save, Plus, Trash2,
  User, Mail, Phone, Calendar, MapPin, Building2, Briefcase, GraduationCap,
  Users, FileText, Eye, Upload, Download, CheckCircle,
  Clock, XCircle, IdCard, CalendarClock, Wallet, ArrowRight,
} from 'lucide-react';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Button } from '@/shared/components/ui/Button';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { consoleAuthorityOf } from '@/shared/permissions/administration';
import { tierOf } from '@/shared/permissions/identity';
import { useEmployee360 } from '../employee360/useEmployee360';
import { useUpdateOnboardingProfile } from '../employee360/useUpdateOnboardingProfile';
import { useUploadOnboardingDocument } from '../employee360/useUploadOnboardingDocument';
import { useAdminUsers, useUserMutations } from '../hooks/useHr';
import { useMasterDepartments } from '@/features/recruitment/api';
import { CREATE_USER_DEPARTMENTS, AUTHORITY_TIER_OPTIONS, type CreateUserRole } from '../components/createUserForm';
import { fmtDate, DEPT_LABELS } from '../constants';
import type {
  OnboardingProfile, OnboardingAddress, OnboardingFamilyMember,
  OnboardingEducation, OnboardingExperience, OnboardingDocument, OnboardingCompanyDocument,
} from '../employee360/types';
import { localToday } from '@/shared/utils/utils';

// ─── Editable draft shape ─────────────────────────────────────────────────────

interface OnboardingDraft {
  firstName: string; lastName: string; email: string; phone: string; alternatePhone: string;
  dateOfBirth: string; gender: string; maritalStatus: string; bloodGroup: string; disability: string;
  aadharNumber: string; panNumber: string; uanNumber: string; esicNumber: string; drivingLicense: string;
  currentAddress: OnboardingAddress; permanentAddress: OnboardingAddress;
  bankName: string; accountNumber: string; ifscCode: string; branchName: string;
  family: OnboardingFamilyMember[];
  education: OnboardingEducation[];
  experience: OnboardingExperience[];
  isFresher: boolean; highestQualification: string; currentSalary: number; skillSet: string;
  designation: string; department: string; dateOfJoining: string; location: string;
  employmentType: string; reportingTo: string;
}

const EMPTY_ADDRESS: OnboardingAddress = { street: '', city: '', state: '', pincode: '' };

function toDraft(profile: OnboardingProfile | null): OnboardingDraft {
  return {
    firstName: profile?.firstName ?? '', lastName: profile?.lastName ?? '', email: profile?.email ?? '',
    phone: profile?.phone ?? '', alternatePhone: profile?.alternatePhone ?? '',
    dateOfBirth: profile?.dateOfBirth?.slice(0, 10) ?? '', gender: profile?.gender ?? '',
    maritalStatus: profile?.maritalStatus ?? '', bloodGroup: profile?.bloodGroup ?? '', disability: profile?.disability ?? '',
    aadharNumber: profile?.aadharNumber ?? '', panNumber: profile?.panNumber ?? '', uanNumber: profile?.uanNumber ?? '',
    esicNumber: profile?.esicNumber ?? '', drivingLicense: profile?.drivingLicense ?? '',
    currentAddress: profile?.currentAddress ?? { ...EMPTY_ADDRESS }, permanentAddress: profile?.permanentAddress ?? { ...EMPTY_ADDRESS },
    bankName: profile?.bankName ?? '', accountNumber: profile?.accountNumber ?? '', ifscCode: profile?.ifscCode ?? '', branchName: profile?.branchName ?? '',
    family: profile?.family ?? [], education: profile?.education ?? [], experience: profile?.experience ?? [],
    isFresher: profile?.isFresher ?? false, highestQualification: profile?.highestQualification ?? '',
    currentSalary: profile?.currentSalary ?? 0, skillSet: (profile?.skillSet ?? []).join(', '),
    designation: profile?.designation ?? '', department: profile?.department ?? '',
    dateOfJoining: profile?.dateOfJoining?.slice(0, 10) ?? '', location: profile?.location ?? '',
    employmentType: profile?.employmentType ?? 'full_time', reportingTo: profile?.reportingTo ?? '',
  };
}

function capitalize(s: string | undefined | null): string {
  if (!s) return '\u2014';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
}

/**
 * Leave balance / attendance / last payslip snapshot \u2014 this page's own "360"
 * name promised a cross-module summary, but until this fix it only ever
 * showed onboarding profile fields. Each fetch is scoped to what the viewer
 * is actually allowed to see: leave balances default to the caller's own
 * unless an HR/superadmin passes user_id; the attendance range endpoint is
 * HR/superadmin-only outright, so a non-privileged self-view falls back to
 * the self-service time-logs endpoint instead; payslips have no per-employee
 * filter on the backend, so the full list is fetched and filtered client-side.
 */
function useEmployeeSnapshot(userId: string | undefined, isSelfView: boolean, isPrivilegedEditor: boolean) {
  const year = new Date().getFullYear();
  const leaveBalances = useQuery({
    queryKey: ['employee360-leave-balances', userId, year],
    enabled: Boolean(userId),
    queryFn: async () => {
      const qs = !isSelfView && userId ? `?year=${year}&user_id=${userId}` : `?year=${year}`;
      const { data } = await api.get<{ balances: Array<{ leave_type_code: string; leave_type_name?: string; allocated: number; used: number; pending: number }> }>(`/api/leave-balances${qs}`);
      return data.balances ?? [];
    },
  });

  const monthStart = `${year}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
  const today = localToday();
  const attendanceRange = useQuery({
    queryKey: ['employee360-attendance-range', userId, monthStart, today],
    enabled: Boolean(userId) && !isSelfView && isPrivilegedEditor,
    queryFn: async () => {
      const { data } = await api.get<{ rows: Array<{ status: string; worked_minutes?: number }> }>(`/api/biometric/v2/attendance/range?from=${monthStart}&to=${today}&userId=${userId}`);
      return data.rows ?? [];
    },
  });
  const myLogs = useQuery({
    queryKey: ['employee360-my-logs'],
    enabled: Boolean(userId) && isSelfView,
    queryFn: async () => {
      const { data } = await api.get<{ logs: Array<{ status?: string; hours?: number; log_date: string }> }>('/api/time-logs?limit=31');
      return data.logs ?? [];
    },
  });

  const payslips = useQuery({
    queryKey: ['employee360-payslips'],
    enabled: Boolean(userId) && (isSelfView || isPrivilegedEditor),
    queryFn: async () => {
      const { data } = await api.get<Array<{ id: string; month_year: string; net_salary?: number; status: string; user?: { id?: string; _id?: string } }>>('/api/v2/hr/payroll/payslips');
      return data.filter((p) => (p.user?._id || p.user?.id) === userId);
    },
  });
  const lastPayslip = payslips.data?.[0] ?? null;

  return { leaveBalances, attendanceRange, myLogs, payslips, lastPayslip };
}

type TabKey = 'basic' | 'employment' | 'contact' | 'banking' | 'certifications' | 'documents';

export function Employee360Page() {
  // `userId` is only present on the admin route (/hr/people/user/:userId).
  // The "My Profile" route (/profile) has no param at all — this same
  // component renders both, defaulting to the caller's own id when absent
  // (2026-08-17). `isMyProfileRoute` distinguishes which URL got us here (for
  // copy/back-link purposes only); `isSelfView` below is the one that actually
  // matters for permissions, since an admin can also land on their own record
  // via the admin route and should get the same self rules either way.
  const { userId: routeUserId } = useParams<{ userId: string }>();
  const currentUser = useUser();
  const isMyProfileRoute = !routeUserId;
  const userId = routeUserId ?? currentUser?.id;

  const [asOf] = useState(() => localToday());
  const query = useEmployee360(userId, asOf);
  const saveProfile = useUpdateOnboardingProfile(userId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<OnboardingDraft>(() => toDraft(null));
  const [activeTab, setActiveTab] = useState<TabKey>('basic');

  const profile = query.data?.onboardingProfile ?? null;
  const isSelfView = Boolean(userId && currentUser?.id === userId);
  const isPrivilegedEditor = currentUser?.role === 'superadmin' || currentUser?.role === 'admin' || currentUser?.department === 'hr';
  const snapshot = useEmployeeSnapshot(userId, isSelfView, isPrivilegedEditor);

  useEffect(() => {
    if (!editing) setDraft(toDraft(profile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const canEdit = isPrivilegedEditor || isSelfView;
  // Employment/reporting fields (designation, department, joining date, etc.)
  // stay locked even in edit mode for a non-privileged employee editing
  // themselves — see SELF_EDITABLE_FIELDS in src/api-hr/people/routes.js,
  // which the backend enforces independently of this frontend flag.
  const isSelfEditRestricted = isSelfView && !isPrivilegedEditor;

  // Access management (authority tier + department transfer) — moved here
  // from the old side drawer on Team & Access (2026-08-16), since row click
  // now opens this full page directly instead of a drawer. Same authorization
  // and gating as before: superadmin only, never on another superadmin or on
  // yourself. `useAdminUsers` is the same list Team & Access itself loads, so
  // this is normally served from cache rather than a fresh fetch.
  const { data: adminUsers = [] } = useAdminUsers();
  const { data: orgDepartments = [] } = useMasterDepartments();
  const { setUserRole, updateUser: updateUserAccess } = useUserMutations();
  const targetAccount = adminUsers.find((u) => u.id === userId);
  const authority = consoleAuthorityOf(currentUser);
  const canManageAccess = Boolean(
    authority.showExecutiveControls
    && targetAccount
    && tierOf(targetAccount.role) !== 'SUPER_ADMIN'
    && targetAccount.id !== currentUser?.id,
  );

  const handleChangeRole = (role: CreateUserRole) => {
    if (!targetAccount) return;
    setUserRole.mutate(
      { id: targetAccount.id, role },
      {
        onSuccess: () => {
          const match = AUTHORITY_TIER_OPTIONS.find((opt) => opt.value === role);
          toast.success(`Role updated to ${match?.label ?? role}.`);
        },
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to change role.')),
      },
    );
  };

  const handleChangeDept = (department: string) => {
    if (!targetAccount) return;
    updateUserAccess.mutate(
      { id: targetAccount.id, payload: { department } },
      {
        onSuccess: () => toast.success('Department updated.'),
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to update department.')),
      },
    );
  };

  if (query.isLoading) return (
    <div className="p-6 flex gap-6">
      <Skeleton className="h-96 w-64 shrink-0" />
      <Skeleton className="h-96 flex-1" />
    </div>
  );

  if (query.isError || !query.data) return (
    <div className="flex h-full items-center justify-center p-6">
      <Card className="max-w-lg"><CardBody className="space-y-3 text-center">
        <ShieldAlert className="mx-auto h-9 w-9 text-warning-text" />
        <h1 className="text-display-sm font-semibold text-on-surface">Employee profile unavailable</h1>
        <p className="text-body-sm text-on-surface-variant">{getApiErrorMessage(query.error, 'This person has not been migrated or you do not have access.')}</p>
        <Link className="inline-flex text-primary hover:underline" to={isMyProfileRoute ? ROUTES.dashboard : ROUTES.hr}>
          {isMyProfileRoute ? 'Return to Dashboard' : 'Return to Team & Access'}
        </Link>
      </CardBody></Card>
    </div>
  );

  const data = query.data;
  const displayName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || String(data.person.displayName ?? 'Employee');

  const startEdit = () => { setDraft(toDraft(profile)); setEditing(true); };
  const cancelEdit = () => { setDraft(toDraft(profile)); setEditing(false); };

  const saveEdit = () => {
    const payload: Record<string, unknown> = {
      ...draft,
      currentSalary: Number(draft.currentSalary) || 0,
      skillSet: draft.skillSet.split(',').map((s) => s.trim()).filter(Boolean),
    };
    saveProfile.mutate(payload, {
      onSuccess: () => { toast.success('Employee profile saved.'); setEditing(false); },
      onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to save changes.')),
    });
  };

  const set = <K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'basic', label: 'Basic Info' },
    { key: 'employment', label: 'Employment' },
    { key: 'contact', label: 'Contact' },
    { key: 'banking', label: 'Banking' },
    { key: 'certifications', label: 'Certifications' },
    { key: 'documents', label: 'Documents' },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-on-surface">{isMyProfileRoute ? 'My Profile' : displayName}</h1>
          <p className="text-sm text-on-surface-variant">
            {isMyProfileRoute ? 'Your employee details. Some fields are managed by HR.' : 'View detailed information and activity for employee.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            editing ? (
              <>
                <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saveProfile.isPending}>
                  <X className="h-3.5 w-3.5" /> Cancel
                </Button>
                <Button size="sm" onClick={saveEdit} loading={saveProfile.isPending}>
                  <Save className="h-3.5 w-3.5" /> Save Changes
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={startEdit}>
                <Pencil className="h-3.5 w-3.5" /> Edit Profile
              </Button>
            )
          )}
          <Link to={isMyProfileRoute ? ROUTES.dashboard : ROUTES.hr} className="flex items-center gap-1.5 text-sm text-on-surface-variant hover:text-on-surface transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </div>

      {/* Main layout: Left sidebar + Right content */}
      <div className="flex flex-col lg:flex-row gap-5">
        {/* ─── Left Sidebar Profile Card ─────────────────────────────────── */}
        <div className="w-full lg:w-72 shrink-0">
          <Card className="sticky top-6">
            <CardBody className="flex flex-col items-center text-center pb-4 pt-6">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-full bg-primary/10 border-2 border-primary/20 flex items-center justify-center mb-3 overflow-hidden">
                {profile?.photoUrl ? (
                  <img src={profile.photoUrl} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <UserRound className="h-12 w-12 text-primary/60" />
                )}
              </div>

              {/* Name & Designation */}
              <h2 className="text-lg font-bold text-on-surface">{displayName}</h2>
              <p className="text-sm text-on-surface-variant">{profile?.designation || '\u2014'}</p>

              {/* Status badge */}
              <Badge tone="success" className="mt-2">Active</Badge>
            </CardBody>

            {/* Quick info list */}
            <div className="border-t border-outline px-5 py-4 space-y-3">
              <SidebarInfoRow icon={IdCard} label="Employee ID" value={profile?.candidateId || '\u2014'} />
              <SidebarInfoRow icon={Mail} label="" value={profile?.email || '\u2014'} />
              <SidebarInfoRow icon={Phone} label="" value={profile?.phone || '\u2014'} />
              <SidebarInfoRow icon={Calendar} label="DOB" value={fmtDate(profile?.dateOfBirth ?? undefined)} />
              <SidebarInfoRow icon={Calendar} label="Joined" value={fmtDate(profile?.dateOfJoining ?? undefined)} />
              <SidebarInfoRow icon={Building2} label="" value={profile?.department || '\u2014'} />
              <SidebarInfoRow icon={MapPin} label="" value={profile?.location || '\u2014'} />
              <SidebarInfoRow icon={Briefcase} label="" value={capitalize(profile?.employmentType)} />
            </div>

            {canManageAccess && targetAccount && (
              <div className="border-t border-outline px-5 py-4 space-y-3">
                <p className="text-label-caps font-semibold uppercase text-on-surface-variant">Access Management</p>
                <div>
                  <label className="mb-1 block text-xs text-on-surface-variant">Authority</label>
                  <Select
                    name="employee360-role"
                    value={AUTHORITY_TIER_OPTIONS.some((o) => o.value === targetAccount.role) ? targetAccount.role : 'employee'}
                    disabled={setUserRole.isPending}
                    onChange={(e) => handleChangeRole(e.target.value as CreateUserRole)}
                    options={AUTHORITY_TIER_OPTIONS.map(({ value, label }) => ({ value, label }))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-on-surface-variant">Department</label>
                  <Select
                    name="employee360-dept"
                    value={targetAccount.department ?? 'sales'}
                    disabled={updateUserAccess.isPending}
                    onChange={(e) => handleChangeDept(e.target.value)}
                    // The live department registry, same source Create User uses.
                    // This was the hardcoded 5-entry CREATE_USER_DEPARTMENTS list,
                    // while PATCH /api/admin/users/:id/department validates against
                    // the registry (15 departments) — 10 legitimate departments were
                    // unreachable from this picker.
                    options={orgDepartments.length > 0
                      ? orgDepartments.map((d) => ({ value: d.value, label: d.label }))
                      : CREATE_USER_DEPARTMENTS.map((value) => ({ value, label: DEPT_LABELS[value] ?? value }))}
                  />
                </div>
                <p className="text-[11px] text-on-surface-variant">
                  Changes here update this user's authority and department across the platform immediately.
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* ─── Right Content Area ────────────────────────────────────────── */}
        <div className="flex-1 min-w-0">
          <SnapshotRow snapshot={snapshot} userId={userId} isSelfView={isSelfView} isPrivilegedEditor={isPrivilegedEditor} />

          {/* Tab navigation */}
          <div className="border-b border-outline mb-5">
            <nav className="flex gap-0 overflow-x-auto" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary text-primary'
                      : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-outline'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="min-h-[400px]">
            {activeTab === 'basic' && <BasicInfoTab profile={profile} draft={draft} editing={editing} set={set} />}
            {activeTab === 'employment' && (
              <EmploymentTab profile={profile} draft={draft} editing={editing} set={set} isSelfEditRestricted={isSelfEditRestricted} />
            )}
            {activeTab === 'contact' && <ContactTab profile={profile} draft={draft} editing={editing} set={set} />}
            {activeTab === 'banking' && <BankingTab profile={profile} draft={draft} editing={editing} set={set} />}
            {activeTab === 'certifications' && <CertificationsTab profile={profile} />}
            {activeTab === 'documents' && <DocumentsTab profile={profile} userId={userId} canEdit={canEdit} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Snapshot row (leave balance / attendance / last payslip) ────────────────

function SnapshotRow({
  snapshot,
  userId,
  isSelfView,
  isPrivilegedEditor,
}: {
  snapshot: ReturnType<typeof useEmployeeSnapshot>;
  userId: string | undefined;
  isSelfView: boolean;
  isPrivilegedEditor: boolean;
}) {
  if (!userId) return null;

  const balances = snapshot.leaveBalances.data ?? [];
  const totalRemaining = balances.reduce((sum, b) => sum + (b.allocated - b.used - b.pending), 0);

  const showAttendance = isSelfView || isPrivilegedEditor;
  const attendanceRows = isSelfView ? null : snapshot.attendanceRange.data;
  const myLogs = snapshot.myLogs.data;
  const presentCount = attendanceRows
    ? attendanceRows.filter((r) => r.status === 'P').length
    : myLogs?.length ?? 0;
  const totalDays = attendanceRows?.length ?? myLogs?.length ?? 0;
  const attendanceLoading = isSelfView ? snapshot.myLogs.isLoading : snapshot.attendanceRange.isLoading;

  const lastPayslip = snapshot.lastPayslip;

  return (
    <div className="grid gap-3 sm:grid-cols-3 mb-5">
      <SnapshotCard
        icon={CalendarClock}
        title="Leave Balance"
        loading={snapshot.leaveBalances.isLoading}
        value={balances.length ? `${totalRemaining} days left` : 'No allocation yet'}
        detail={balances.length ? `Across ${balances.length} leave type${balances.length === 1 ? '' : 's'}` : 'Contact HR to get leave allocated'}
        linkTo={ROUTES.leaves}
        linkLabel="View leaves"
      />
      {showAttendance ? (
        <SnapshotCard
          icon={Clock}
          title="Attendance"
          loading={attendanceLoading}
          value={totalDays > 0 ? `${presentCount}/${totalDays} days present` : 'No records yet'}
          detail={isSelfView ? 'Last 31 logged days' : 'This month'}
          linkTo={ROUTES.attendance}
          linkLabel="View attendance"
        />
      ) : (
        <SnapshotCard icon={Clock} title="Attendance" loading={false} value="—" detail="Not available for this view" linkTo={ROUTES.attendance} linkLabel="View attendance" />
      )}
      <SnapshotCard
        icon={Wallet}
        title="Last Payslip"
        loading={snapshot.payslips.isLoading}
        value={lastPayslip ? `${lastPayslip.month_year}` : 'No payslip yet'}
        detail={lastPayslip ? `${capitalize(lastPayslip.status)}${lastPayslip.net_salary ? ` · ₹${lastPayslip.net_salary.toLocaleString('en-IN')}` : ''}` : 'Generated after payroll runs'}
        linkTo={ROUTES.payroll}
        linkLabel="View payroll"
      />
    </div>
  );
}

function SnapshotCard({
  icon: Icon,
  title,
  value,
  detail,
  loading,
  linkTo,
  linkLabel,
}: {
  icon: typeof Clock;
  title: string;
  value: string;
  detail: string;
  loading: boolean;
  linkTo: string;
  linkLabel: string;
}) {
  return (
    <Card>
      <CardBody className="space-y-2">
        <div className="flex items-center gap-2 text-on-surface-variant">
          <Icon className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wide">{title}</span>
        </div>
        {loading ? (
          <Skeleton className="h-6 w-24" />
        ) : (
          <p className="text-lg font-bold text-on-surface">{value}</p>
        )}
        <p className="text-xs text-on-surface-variant">{detail}</p>
        <Link to={linkTo} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          {linkLabel} <ArrowRight className="h-3 w-3" />
        </Link>
      </CardBody>
    </Card>
  );
}

// ─── Sidebar info row ─────────────────────────────────────────────────────────

function SidebarInfoRow({ icon: Icon, label, value }: { icon: typeof User; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <Icon className="h-4 w-4 shrink-0 text-on-surface-variant/70" />
      <span className="text-on-surface">
        {label && <span className="text-on-surface-variant">{label}: </span>}
        {value}
      </span>
    </div>
  );
}

// ─── Tab: Basic Info ──────────────────────────────────────────────────────────

interface TabProps {
  profile: OnboardingProfile | null;
  draft: OnboardingDraft;
  editing: boolean;
  set: <K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) => void;
  /**
   * True when a non-privileged employee is editing their OWN profile.
   * Only EmploymentTab reads this — the employment/reporting fields below
   * mirror SELF_EDITABLE_FIELDS's exclusions in
   * src/api-hr/people/routes.js, which enforces the same restriction
   * server-side regardless of what this flag does on the client.
   */
  isSelfEditRestricted?: boolean;
}

function BasicInfoTab({ profile, draft, editing, set }: TabProps) {
  if (editing) {
    return (
      <ContentCard title="Basic Information">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="First Name" value={draft.firstName} onChange={(e) => set('firstName', e.target.value)} />
          <Input label="Last Name" value={draft.lastName} onChange={(e) => set('lastName', e.target.value)} />
          <Input label="Email" type="email" value={draft.email} onChange={(e) => set('email', e.target.value)} />
          <Input label="Phone" value={draft.phone} onChange={(e) => set('phone', e.target.value)} />
          <Input label="Employee ID" value={profile?.candidateId ?? ''} disabled />
          <Input label="Date of Birth" type="date" value={draft.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
          <Select label="Gender" value={draft.gender} onChange={(e) => set('gender', e.target.value)}
            options={[{ value: '', label: 'Select\u2026' }, { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]} />
          <Select label="Marital Status" value={draft.maritalStatus} onChange={(e) => set('maritalStatus', e.target.value)}
            options={[{ value: '', label: 'Select\u2026' }, { value: 'single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'divorced', label: 'Divorced' }, { value: 'widowed', label: 'Widowed' }]} />
          <Input label="Blood Group" value={draft.bloodGroup} onChange={(e) => set('bloodGroup', e.target.value)} placeholder="e.g. B+" />
          <Input label="Disability" value={draft.disability} onChange={(e) => set('disability', e.target.value)} placeholder="None" />
        </div>
      </ContentCard>
    );
  }

  return (
    <ContentCard title="Basic Information">
      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
        <FieldDisplay label="Full Name" value={`${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || '\u2014'} />
        <FieldDisplay label="Employee ID" value={profile?.candidateId || '\u2014'} />
        <FieldDisplay label="Email" value={profile?.email || '\u2014'} />
        <FieldDisplay label="Phone Number" value={profile?.phone || '\u2014'} />
        <FieldDisplay label="Employee Code" value={profile?.candidateId || '\u2014'} />
        <FieldDisplay label="Date of Birth" value={fmtDate(profile?.dateOfBirth ?? undefined)} />
        <FieldDisplay label="Gender" value={capitalize(profile?.gender)} />
        <FieldDisplay label="Marital Status" value={capitalize(profile?.maritalStatus)} />
        <FieldDisplay label="Blood Group" value={profile?.bloodGroup || '\u2014'} />
        <FieldDisplay label="Disability" value={profile?.disability || 'None'} />
      </div>
    </ContentCard>
  );
}

// ─── Tab: Employment ──────────────────────────────────────────────────────────

function EmploymentTab({ profile, draft, editing, set, isSelfEditRestricted }: TabProps) {
  if (editing) {
    return (
      <ContentCard title="Employment Details">
        {isSelfEditRestricted && (
          <p className="mb-4 text-xs text-on-surface-variant bg-surface-variant/40 rounded-md px-3 py-2">
            Designation, department, joining date, location, employment type, and reporting manager are managed by HR — contact HR to update these.
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Designation" value={draft.designation} onChange={(e) => set('designation', e.target.value)} disabled={isSelfEditRestricted} />
          <Input label="Department" value={draft.department} onChange={(e) => set('department', e.target.value)} disabled={isSelfEditRestricted} />
          <Input label="Date of Joining" type="date" value={draft.dateOfJoining} onChange={(e) => set('dateOfJoining', e.target.value)} disabled={isSelfEditRestricted} />
          <Input label="Location" value={draft.location} onChange={(e) => set('location', e.target.value)} disabled={isSelfEditRestricted} />
          <Select label="Employment Type" value={draft.employmentType} onChange={(e) => set('employmentType', e.target.value)} disabled={isSelfEditRestricted}
            options={[{ value: 'full_time', label: 'Full Time' }, { value: 'part_time', label: 'Part Time' }, { value: 'contract', label: 'Contract' }, { value: 'intern', label: 'Intern' }]} />
          <Input label="Reporting To" value={draft.reportingTo} onChange={(e) => set('reportingTo', e.target.value)} disabled={isSelfEditRestricted} />
          <Input label="Highest Qualification" value={draft.highestQualification} onChange={(e) => set('highestQualification', e.target.value)} />
          <Input label="Skills (comma separated)" value={draft.skillSet} onChange={(e) => set('skillSet', e.target.value)} />
        </div>

        {/* Experience section in edit mode */}
        <div className="mt-6 pt-5 border-t border-outline">
          <h4 className="text-sm font-semibold text-on-surface mb-3">Work Experience</h4>
          <label className="flex items-center gap-2 text-sm text-on-surface mb-3">
            <input type="checkbox" checked={draft.isFresher} onChange={(e) => set('isFresher', e.target.checked)} className="rounded" />
            Fresher (no previous work experience)
          </label>
          {!draft.isFresher && <ExperienceEditor rows={draft.experience} onChange={(rows) => set('experience', rows)} />}
        </div>

        {/* Education section in edit mode */}
        <div className="mt-6 pt-5 border-t border-outline">
          <h4 className="text-sm font-semibold text-on-surface mb-3">Education</h4>
          <EducationEditor rows={draft.education} onChange={(rows) => set('education', rows)} />
        </div>

        {/* Family section in edit mode */}
        <div className="mt-6 pt-5 border-t border-outline">
          <h4 className="text-sm font-semibold text-on-surface mb-3">Family Members</h4>
          <FamilyEditor rows={draft.family} onChange={(rows) => set('family', rows)} />
        </div>
      </ContentCard>
    );
  }

  return (
    <div className="space-y-5">
      <ContentCard title="Employment Details">
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          <FieldDisplay label="Designation" value={profile?.designation || '\u2014'} />
          <FieldDisplay label="Department" value={profile?.department || '\u2014'} />
          <FieldDisplay label="Date of Joining" value={fmtDate(profile?.dateOfJoining ?? undefined)} />
          <FieldDisplay label="Location" value={profile?.location || '\u2014'} />
          <FieldDisplay label="Employment Type" value={capitalize(profile?.employmentType)} />
          <FieldDisplay label="Reporting To" value={profile?.reportingTo || '\u2014'} />
          <FieldDisplay label="Highest Qualification" value={profile?.highestQualification || '\u2014'} />
          <FieldDisplay label="Skills" value={(profile?.skillSet ?? []).join(', ') || '\u2014'} />
        </div>
      </ContentCard>

      {/* Work Experience */}
      <ContentCard title="Work Experience">
        {profile?.isFresher ? (
          <div className="text-center py-4">
            <Badge tone="info">Fresher</Badge>
            <p className="mt-2 text-sm text-on-surface-variant">No previous work experience.</p>
          </div>
        ) : !profile?.experience?.length ? (
          <p className="text-sm text-on-surface-variant py-4 text-center">No work experience recorded.</p>
        ) : (
          <div className="space-y-3">
            {profile.experience.map((exp, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-outline/50 bg-surface-container-low/30">
                <Briefcase className="h-5 w-5 mt-0.5 text-primary/60 shrink-0" />
                <div>
                  <p className="font-medium text-on-surface text-sm">{exp.occupation || 'Role not specified'}</p>
                  <p className="text-xs text-on-surface-variant">{exp.company}{exp.duration ? ` \u00B7 ${exp.duration}` : ''}</p>
                  {exp.reasonForLeaving && <p className="text-xs text-on-surface-variant mt-0.5">Left: {exp.reasonForLeaving}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </ContentCard>

      {/* Education */}
      <ContentCard title="Education">
        {!profile?.education?.length ? (
          <p className="text-sm text-on-surface-variant py-4 text-center">No education records.</p>
        ) : (
          <div className="space-y-3">
            {profile.education.map((edu, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-outline/50 bg-surface-container-low/30">
                <GraduationCap className="h-5 w-5 mt-0.5 text-primary/60 shrink-0" />
                <div>
                  <p className="font-medium text-on-surface text-sm">{edu.degree}</p>
                  <p className="text-xs text-on-surface-variant">{edu.schoolName}{edu.fieldOfStudy ? ` \u2014 ${edu.fieldOfStudy}` : ''}</p>
                  {edu.completionDate && <p className="text-xs text-on-surface-variant">{fmtDate(edu.completionDate)}</p>}
                  {edu.additionalNotes && <p className="text-xs text-on-surface-variant mt-0.5">{edu.additionalNotes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </ContentCard>

      {/* Family */}
      <ContentCard title="Family Members">
        {!profile?.family?.length ? (
          <p className="text-sm text-on-surface-variant py-4 text-center">No family members recorded.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {profile.family.map((f, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-outline/50 bg-surface-container-low/30">
                <Users className="h-5 w-5 text-primary/60 shrink-0" />
                <div>
                  <p className="font-medium text-on-surface text-sm">{f.name || '\u2014'}</p>
                  <p className="text-xs text-on-surface-variant capitalize">{f.relation}{f.contact ? ` \u00B7 ${f.contact}` : ''}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </ContentCard>
    </div>
  );
}

// ─── Tab: Contact ─────────────────────────────────────────────────────────────

function ContactTab({ profile, draft, editing, set }: TabProps) {
  if (editing) {
    return (
      <div className="space-y-5">
        <ContentCard title="Contact Information">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Phone" value={draft.phone} onChange={(e) => set('phone', e.target.value)} />
            <Input label="Alternate Phone" value={draft.alternatePhone} onChange={(e) => set('alternatePhone', e.target.value)} />
            <Input label="Email" value={draft.email} onChange={(e) => set('email', e.target.value)} className="sm:col-span-2" />
          </div>
        </ContentCard>
        <ContentCard title="Current Address">
          <AddressFields addr={draft.currentAddress} onChange={(addr) => set('currentAddress', addr)} />
        </ContentCard>
        <ContentCard title="Permanent Address">
          <AddressFields addr={draft.permanentAddress} onChange={(addr) => set('permanentAddress', addr)} />
        </ContentCard>
        <ContentCard title="Identity Documents">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Aadhar Number" value={draft.aadharNumber} onChange={(e) => set('aadharNumber', e.target.value)} />
            <Input label="PAN Number" value={draft.panNumber} onChange={(e) => set('panNumber', e.target.value)} />
            <Input label="UAN Number" value={draft.uanNumber} onChange={(e) => set('uanNumber', e.target.value)} />
            <Input label="ESIC Number" value={draft.esicNumber} onChange={(e) => set('esicNumber', e.target.value)} />
            <Input label="Driving License" value={draft.drivingLicense} onChange={(e) => set('drivingLicense', e.target.value)} />
          </div>
        </ContentCard>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ContentCard title="Contact Information">
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          <FieldDisplay label="Phone" value={profile?.phone || '\u2014'} />
          <FieldDisplay label="Alternate Phone" value={profile?.alternatePhone || '\u2014'} />
          <FieldDisplay label="Email" value={profile?.email || '\u2014'} />
        </div>
      </ContentCard>

      <ContentCard title="Current Address">
        <AddressReadBlock addr={profile?.currentAddress} />
      </ContentCard>

      <ContentCard title="Permanent Address">
        <AddressReadBlock addr={profile?.permanentAddress} />
      </ContentCard>

      <ContentCard title="Identity Documents">
        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          <FieldDisplay label="Aadhar Number" value={profile?.aadharNumber || '\u2014'} />
          <FieldDisplay label="PAN Number" value={profile?.panNumber || '\u2014'} />
          <FieldDisplay label="UAN Number" value={profile?.uanNumber || '\u2014'} />
          <FieldDisplay label="ESIC Number" value={profile?.esicNumber || '\u2014'} />
          <FieldDisplay label="Driving License" value={profile?.drivingLicense || '\u2014'} />
        </div>
      </ContentCard>
    </div>
  );
}

// ─── Tab: Banking ─────────────────────────────────────────────────────────────

function BankingTab({ profile, draft, editing, set }: TabProps) {
  if (editing) {
    return (
      <ContentCard title="Bank Account Details">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Bank Name" value={draft.bankName} onChange={(e) => set('bankName', e.target.value)} />
          <Input label="Account Number" value={draft.accountNumber} onChange={(e) => set('accountNumber', e.target.value)} />
          <Input label="IFSC Code" value={draft.ifscCode} onChange={(e) => set('ifscCode', e.target.value)} />
          <Input label="Branch Name" value={draft.branchName} onChange={(e) => set('branchName', e.target.value)} />
        </div>
      </ContentCard>
    );
  }

  return (
    <ContentCard title="Bank Account Details">
      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
        <FieldDisplay label="Bank Name" value={profile?.bankName || '\u2014'} />
        <FieldDisplay label="Account Number" value={profile?.accountNumber || '\u2014'} />
        <FieldDisplay label="IFSC Code" value={profile?.ifscCode || '\u2014'} />
        <FieldDisplay label="Branch" value={profile?.branchName || '\u2014'} />
      </div>
    </ContentCard>
  );
}

// ─── Tab: Certifications (Company Documents - Offer Letter, Appointment, etc.) ─

function CertificationsTab({ profile }: { profile: OnboardingProfile | null }) {
  const companyDocs = profile?.companyDocuments ?? [];
  if (companyDocs.length === 0) {
    return <ContentCard title="Employee Certifications"><EmptyState message="No company documents generated yet." /></ContentCard>;
  }

  return (
    <ContentCard title="Employee Certifications">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {companyDocs.map((doc) => (
          <CompanyDocCard key={doc.code} doc={doc} />
        ))}
      </div>
    </ContentCard>
  );
}

function CompanyDocCard({ doc }: { doc: OnboardingCompanyDocument }) {
  const hasFile = doc.fileUrl && doc.status !== 'not_generated';
  return (
    <div className="rounded-xl border border-outline bg-surface p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-on-surface text-sm leading-tight">{doc.label}</p>
          <p className="text-xs text-on-surface-variant mt-0.5">
            {doc.status === 'not_generated' ? 'Not generated' : doc.status === 'acknowledged' ? 'Acknowledged' : capitalize(doc.status)}
          </p>
        </div>
      </div>
      {hasFile ? (
        <a
          href={doc.fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-on-surface-variant border border-outline rounded-lg px-3 py-1.5 hover:bg-surface-variant transition-colors w-fit"
        >
          <Download className="h-3.5 w-3.5" /> Download
        </a>
      ) : (
        <span className="text-xs text-on-surface-variant italic">Not available yet</span>
      )}
    </div>
  );
}

// ─── Tab: Documents (Candidate Documents with View/Upload) ────────────────────

function DocumentsTab({ profile, userId, canEdit }: { profile: OnboardingProfile | null; userId: string | undefined; canEdit: boolean }) {
  const uploadDoc = useUploadOnboardingDocument(userId);
  const [uploadingCode, setUploadingCode] = useState<string | null>(null);
  const candidateDocs = profile?.candidateDocuments ?? [];
  if (candidateDocs.length === 0) {
    return <ContentCard title="Documents"><EmptyState message="No documents on file. Documents will appear here after E-Joining." /></ContentCard>;
  }

  const handleUpload = (code: string, file: File) => {
    setUploadingCode(code);
    uploadDoc.mutate(
      { code, file },
      {
        onSuccess: () => toast.success('Document uploaded.'),
        onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Upload failed.')),
        onSettled: () => setUploadingCode(null),
      },
    );
  };

  return (
    <ContentCard title="Documents">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {candidateDocs.map((doc) => (
          <DocumentCard
            key={doc.code}
            doc={doc}
            canEdit={canEdit}
            uploading={uploadingCode === doc.code}
            onUpload={(file) => handleUpload(doc.code, file)}
          />
        ))}
      </div>
    </ContentCard>
  );
}

function DocumentCard({
  doc,
  canEdit,
  uploading,
  onUpload,
}: {
  doc: OnboardingDocument;
  canEdit: boolean;
  uploading: boolean;
  onUpload: (file: File) => void;
}) {
  const statusConfig: Record<string, { icon: typeof CheckCircle; color: string; bg: string; label: string }> = {
    verified: { icon: CheckCircle, color: 'text-success-text', bg: 'bg-success/10 dark:bg-success', label: 'Verified' },
    submitted: { icon: Clock, color: 'text-info-text', bg: 'bg-info/10 dark:bg-info', label: 'Submitted' },
    pending: { icon: Clock, color: 'text-warning-text', bg: 'bg-warning/10 dark:bg-warning', label: 'Pending' },
    rejected: { icon: XCircle, color: 'text-danger-text', bg: 'bg-danger/10 dark:bg-danger', label: 'Rejected' },
    not_applicable: { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-50 dark:bg-gray-800/30', label: 'N/A' },
  };

  const cfg = statusConfig[doc.status] || statusConfig.pending;
  const StatusIcon = cfg.icon;
  const hasFile = !!doc.fileUrl;

  return (
    <div className="rounded-xl border border-outline bg-surface p-4 flex flex-col justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-surface-variant/60 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 text-on-surface-variant" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-on-surface text-sm leading-tight">{doc.label}</p>
          <p className="text-xs text-on-surface-variant mt-0.5">No expiry date</p>
          <span className={`inline-flex items-center gap-1 text-xs font-medium mt-1.5 px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
            <StatusIcon className="h-3 w-3" />
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 pt-1">
        {hasFile && (
          <a
            href={doc.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg border border-outline hover:bg-surface-variant transition-colors"
            title="View document"
          >
            <Eye className="h-4 w-4 text-on-surface-variant" />
          </a>
        )}
        {canEdit && !hasFile && doc.status === 'pending' && (
          <DocumentUploadButton uploading={uploading} onUpload={onUpload} title="Upload document" tone="default" />
        )}
        {canEdit && doc.status === 'rejected' && (
          <DocumentUploadButton uploading={uploading} onUpload={onUpload} title="Re-upload document" tone="danger" />
        )}
        {hasFile && (doc.status === 'submitted' || doc.status === 'verified') && (
          <a
            href={doc.fileUrl}
            download
            className="p-2 rounded-lg border border-outline hover:bg-surface-variant transition-colors"
            title="Download"
          >
            <Download className="h-4 w-4 text-on-surface-variant" />
          </a>
        )}
      </div>
    </div>
  );
}

function DocumentUploadButton({
  uploading,
  onUpload,
  title,
  tone,
}: {
  uploading: boolean;
  onUpload: (file: File) => void;
  title: string;
  tone: 'default' | 'danger';
}) {
  return (
    <label
      className={`p-2 rounded-lg border border-outline hover:bg-surface-variant transition-colors cursor-pointer ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
      title={title}
    >
      <Upload className={`h-4 w-4 ${tone === 'danger' ? 'text-danger-text' : 'text-on-surface-variant'}`} />
      <input
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ''; // allow re-selecting the same file after a failed upload
          if (file) onUpload(file);
        }}
      />
    </label>
  );
}

// ─── Shared subcomponents ─────────────────────────────────────────────────────

function ContentCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardBody>
        <h3 className="text-base font-bold text-on-surface mb-5 pb-3 border-b border-outline">{title}</h3>
        {children}
      </CardBody>
    </Card>
  );
}

function FieldDisplay({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-on-surface-variant mb-1">{label}</p>
      <p className="text-sm font-semibold text-on-surface">{value}</p>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-8 text-center">
      <p className="text-sm text-on-surface-variant">{message}</p>
    </div>
  );
}

function AddressReadBlock({ addr }: { addr?: OnboardingAddress }) {
  if (!addr || (!addr.street && !addr.city)) {
    return <p className="text-sm text-on-surface-variant">Not yet filled.</p>;
  }
  return (
    <div className="text-sm text-on-surface space-y-0.5">
      {addr.street && <p>{addr.street}</p>}
      <p>{[addr.city, addr.state].filter(Boolean).join(', ')}{addr.pincode ? ` \u2014 ${addr.pincode}` : ''}</p>
    </div>
  );
}

// ─── Edit-mode subcomponents ──────────────────────────────────────────────────

function AddressFields({ addr, onChange }: { addr: OnboardingAddress; onChange: (addr: OnboardingAddress) => void }) {
  const upd = (key: keyof OnboardingAddress, value: string) => onChange({ ...addr, [key]: value });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Input label="Street" value={addr.street} onChange={(e) => upd('street', e.target.value)} className="col-span-2" />
      <Input label="City" value={addr.city} onChange={(e) => upd('city', e.target.value)} />
      <Input label="State" value={addr.state} onChange={(e) => upd('state', e.target.value)} />
      <Input label="Pincode" value={addr.pincode} onChange={(e) => upd('pincode', e.target.value)} />
    </div>
  );
}

function FamilyEditor({ rows, onChange }: { rows: OnboardingFamilyMember[]; onChange: (rows: OnboardingFamilyMember[]) => void }) {
  const update = (i: number, patch: Partial<OnboardingFamilyMember>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { name: '', relation: 'other', contact: '' }]);

  return (
    <div className="space-y-3">
      {rows.map((f, i) => (
        <div key={i} className="grid grid-cols-[1fr_140px_1fr_auto] gap-2 items-end">
          <Input label={i === 0 ? 'Name' : undefined} value={f.name} onChange={(e) => update(i, { name: e.target.value })} />
          <Select
            label={i === 0 ? 'Relation' : undefined}
            value={f.relation}
            onChange={(e) => update(i, { relation: e.target.value as OnboardingFamilyMember['relation'] })}
            options={[
              { value: 'father', label: 'Father' }, { value: 'mother', label: 'Mother' }, { value: 'spouse', label: 'Spouse' },
              { value: 'brother', label: 'Brother' }, { value: 'sister', label: 'Sister' }, { value: 'child', label: 'Child' }, { value: 'other', label: 'Other' },
            ]}
          />
          <Input label={i === 0 ? 'Contact' : undefined} value={f.contact} onChange={(e) => update(i, { contact: e.target.value })} />
          <Button variant="outline" size="icon" onClick={() => remove(i)} aria-label="Remove family member"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add}><Plus className="h-3.5 w-3.5" /> Add family member</Button>
    </div>
  );
}

function EducationEditor({ rows, onChange }: { rows: OnboardingEducation[]; onChange: (rows: OnboardingEducation[]) => void }) {
  const update = (i: number, patch: Partial<OnboardingEducation>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { schoolName: '', degree: '', fieldOfStudy: '', completionDate: '', additionalNotes: '' }]);

  return (
    <div className="space-y-3">
      {rows.map((edu, i) => (
        <div key={i} className="rounded-lg border border-outline bg-surface-container-low/30 p-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-on-surface-variant">Entry {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="School / College" value={edu.schoolName} onChange={(e) => update(i, { schoolName: e.target.value })} />
            <Input placeholder="Degree" value={edu.degree} onChange={(e) => update(i, { degree: e.target.value })} />
            <Input placeholder="Field of study" value={edu.fieldOfStudy} onChange={(e) => update(i, { fieldOfStudy: e.target.value })} />
            <Input type="date" value={edu.completionDate?.slice(0, 10) ?? ''} onChange={(e) => update(i, { completionDate: e.target.value })} />
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add}><Plus className="h-3.5 w-3.5" /> Add education entry</Button>
    </div>
  );
}

function ExperienceEditor({ rows, onChange }: { rows: OnboardingExperience[]; onChange: (rows: OnboardingExperience[]) => void }) {
  const update = (i: number, patch: Partial<OnboardingExperience>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { company: '', occupation: '', summary: '', duration: '', reasonForLeaving: '', currentlyWorkHere: false }]);

  return (
    <div className="space-y-3">
      {rows.map((exp, i) => (
        <div key={i} className="rounded-lg border border-outline bg-surface-container-low/30 p-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-on-surface-variant">Company {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="Company name" value={exp.company} onChange={(e) => update(i, { company: e.target.value })} />
            <Input placeholder="Job title / role" value={exp.occupation} onChange={(e) => update(i, { occupation: e.target.value })} />
            <Input placeholder="Duration (e.g. 2 years)" value={exp.duration} onChange={(e) => update(i, { duration: e.target.value })} />
            <Input placeholder="Reason for leaving" value={exp.reasonForLeaving} onChange={(e) => update(i, { reasonForLeaving: e.target.value })} />
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add}><Plus className="h-3.5 w-3.5" /> Add work experience</Button>
    </div>
  );
}
